import React from 'react';
import {
  Box, Typography, Container, Button, Stack,
} from '@mui/material';
import posthog from 'posthog-js';
import { Crisp } from 'crisp-sdk-web';
import { type TFunction } from 'i18next';

type Props = {
  children: React.ReactNode;
  t: TFunction;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class RawErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);

    posthog.capture('frontend_error', {
      error_message: error.message,
      stack_trace: error.stack,
      component_stack: info.componentStack,
      location: window.location.href,
    });
  }

  render() {
    const { hasError, error } = this.state;
    const isInsertBeforeError = error?.message?.includes(
      "Failed to execute 'insertBefore' on 'Node'",
    );

    if (hasError && isInsertBeforeError) {
      return (
        <Container maxWidth="sm">
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10}>
            <img
              src="/static/appLogo.png"
              alt="ManageMate Logo"
              style={{ width: 100, marginBottom: 32 }}
            />

            <img
              src="/static/error_fallback.png"
              alt="Rendering error"
              style={{ width: '100%', maxWidth: 300, marginBottom: 24 }}
            />

            <Typography variant="h4" color="error" gutterBottom align="center">
              Rendering Issue Detected
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" mb={4}>
              We detected an issue on this page. Our development team is working to resolve it as soon as possible.
              <br />
              You can try refreshing the page — in some cases, that resolves the problem.
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button onClick={() => Crisp.chat.open()} sx={{ mr: 2 }} variant="outlined">
                {/* eslint-disable-next-line react/destructuring-assignment */}
                {this.props.t('contactSupport')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
              >
                {/* eslint-disable-next-line react/destructuring-assignment */}
                {this.props.t('refreshPage')}
              </Button>
            </Stack>
          </Box>
        </Container>
      );
    }

    // eslint-disable-next-line react/destructuring-assignment
    return this.props.children;
  }
}
